import qs from 'qs';

import { dateUtils } from '~/routes/utils';
import { appointmentDraftSelectors } from '~/api';
import { formatSlotsData } from '~/components/calendar-with-filters/helpers';

import { slotsApi } from './slots.api';

const actionPrefix = (actionType) => `SelectDate/${actionType}`;

export const SET_SLOTS = actionPrefix`SET_SLOTS`;
export const setSlots = (slots) => ({
  type: SET_SLOTS,
  payload: slots,
});

export const SET_NEXT_SLOTS = actionPrefix`SET_NEXT_SLOTS`;
export const setNextSlots = (slots) => ({
  type: SET_NEXT_SLOTS,
  payload: slots,
});

export const SET_PREVIOUS_SLOTS = actionPrefix`SET_PREVIOUS_SLOTS`;
export const setPreviousSlots = (slots) => ({
  type: SET_PREVIOUS_SLOTS,
  payload: slots,
});

export const SET_FILTERS = actionPrefix`SET_FILTERS`;
export const setFilters = (filter) => ({
  type: SET_FILTERS,
  payload: filter,
});

export const createQueryParams = (
  { appointmentType: { id: appointmentTypeId }, products, address: { postcode } },
  rangeStartDate,
  rangeEndDate,
) => {
  const stringWithProducts = products
    .map(({ id, quantity, selectionType }) => {
      const selectionTypeString = selectionType ? `,"selectionTypeId":"${selectionType.id}"` : '';
      return `{"id":"${id}","quantity":${quantity}${selectionTypeString}}`;
    })
    .join(',');

  const productsStringify = encodeURIComponent(`[${stringWithProducts}]`);

  return qs.stringify(
    {
      appointmentTypeId,
      startDate: rangeStartDate,
      endDate: rangeEndDate,
      postcode,
      products: productsStringify,
    },
    { skipNulls: true, encode: false },
  );
};

export const formatSlots = (resolvedSlots, startDate, eligibleSlotsFilter) =>
  resolvedSlots.map((monthSlot, index) => {
    const monthStartDate = index === 0 ? startDate : undefined;
    return formatSlotsData(monthSlot.slots, monthStartDate, eligibleSlotsFilter);
  });

const hasAvailableSlots = (formattedSlots) =>
  formattedSlots.some((slot) => slot.dates.some((date) => date.times.length));

export const loadSlots = async (
  dispatch,
  getState,
  startDate,
  slotsAction,
  eligibleSlotsFilter,
  monthCount = 1,
) => {
  const state = getState();
  const appointmentDraft = appointmentDraftSelectors.selectAppointmentDraft(state);

  // get the urls to pass to the fetch function 'fetchJSONFromAPI'
  const [firstSlotUrl, secondSlotUrl] = Object.entries(
    dateUtils.getMonthsRangesForDuration(startDate, monthCount),
  ).reduce((params, [, { startDate: rangeStartDate, endDate: rangeEndDate }]) => {
    const queryUrl = createQueryParams(appointmentDraft, rangeStartDate, rangeEndDate);
    return [...params, queryUrl];
  }, []);

  const firstSlotRequest = slotsApi.fetchJSONFromAPI(firstSlotUrl, {});
  // the first request is resolved and formatted
  const formattedSlots = formatSlots([await firstSlotRequest], startDate, eligibleSlotsFilter);

  if (!secondSlotUrl) {
    // if there is no next month required
    dispatch(slotsAction(formattedSlots));
    return;
  }
  // no available slots for current month
  if (!hasAvailableSlots(formattedSlots)) {
    const resolvedOtherSlots = slotsApi.fetchJSONFromAPI(secondSlotUrl, {});
    const otherFormattedSlots = formatSlots(
      [await resolvedOtherSlots],
      undefined,
      eligibleSlotsFilter,
    );
    dispatch(slotsAction([...formattedSlots, ...otherFormattedSlots]));
    return;
  }

  if (secondSlotUrl) {
    // dispatch first month slots to paint the DOM before the second request
    dispatch(slotsAction(formattedSlots));
    // fetch and dispatch next month
    const resolvedOtherSlots = slotsApi.fetchJSONFromAPI(secondSlotUrl, {});
    const otherFormattedSlots = formatSlots(
      [await resolvedOtherSlots],
      undefined,
      eligibleSlotsFilter,
    );
    dispatch(setNextSlots(otherFormattedSlots));
  }
};

export const loadInitialSlots =
  (startDate, monthCount, eligibleSlotsFilter) => (dispatch, getState) =>
    loadSlots(dispatch, getState, startDate, setSlots, eligibleSlotsFilter, monthCount);

export const loadPreviousSlots = (startDate, eligibleSlotsFilter) => (dispatch, getState) =>
  loadSlots(dispatch, getState, startDate, setPreviousSlots, eligibleSlotsFilter);

export const loadNextSlots = (startDate, eligibleSlotsFilter) => (dispatch, getState) =>
  loadSlots(dispatch, getState, startDate, setNextSlots, eligibleSlotsFilter);

export const slotsActions = {
  loadSlots,
};
